import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { TableName } from '../../constants/table';
import { updateEntity } from '../../lib/services/api-service';

interface StockMappingsDialogProps {
  open: boolean;
  onClose: () => void;
  stockId: string;
  stockName: string;
  stockOCR: string;
  partitionKey: string;
  matchingStockAliases: string[];
  onMappingsChanged: () => void;
}

const StockMappingsDialog: React.FC<StockMappingsDialogProps> = ({
  open,
  onClose,
  stockId,
  stockName,
  partitionKey,
  matchingStockAliases,
  onMappingsChanged,
}) => {
  const [mappings, setMappings] = useState<string[]>([]);
  const [deleting, setDeleting] = useState<string | null>(null);

  const parsedAliases = useMemo(() => {
    if (!matchingStockAliases) return [];

    if (Array.isArray(matchingStockAliases)) {
      return matchingStockAliases;
    }

    if (typeof matchingStockAliases === 'string') {
      try {
        return JSON.parse(matchingStockAliases);
      } catch {
        return [matchingStockAliases];
      }
    }

    return [];
  }, [matchingStockAliases]);

  useEffect(() => {
    if (open) {
      setMappings(parsedAliases.filter((alias: string) => alias !== stockName));
    }
  }, [open, parsedAliases, stockName]);

  const handleDeleteMapping = async (aliasToDelete: string) => {
    setDeleting(aliasToDelete);
    try {
      const updatedAliases = mappings.filter(alias => alias !== aliasToDelete);

      // When saving, include the stock name back in the aliases array
      await updateEntity(TableName.StockMaster, {
        PartitionKey: partitionKey,
        RowKey: stockId,
        MatchingStockAliases: JSON.stringify([stockName, ...updatedAliases]),
      });

      setMappings(updatedAliases);
      toast.success(`Deleted mapping: ${aliasToDelete}`);
      onMappingsChanged();
    } catch (error) {
      console.error('Error deleting mapping:', error);
      toast.error('Failed to delete mapping');
    } finally {
      setDeleting(null);
    }
  };

  // Total count includes the stock name itself + other mappings
  const totalMappingsCount = 1 + mappings.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div>
          Mappings for <strong>{stockName}</strong>
        </div>
        <div style={{ fontSize: '0.875rem', color: 'gray' }}>
          {totalMappingsCount} mapping(s) found
        </div>
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Stock Alias (OCR Name)</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* First row: The stock item itself (not deletable) */}
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>
                  {stockName}
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: '0.75rem',
                      color: 'gray',
                    }}>
                    (Primary)
                  </span>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Primary stock name cannot be deleted">
                    <span>
                      <IconButton size="small" color="default" disabled>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
              {/* Other mappings (deletable) */}
              {mappings.map((alias, index) => (
                <TableRow key={`${alias}-${index}`}>
                  <TableCell>{alias}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete mapping">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deleting === alias}
                          onClick={() => handleDeleteMapping(alias)}>
                          {deleting === alias ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockMappingsDialog;
